/**
 * apps/web :: hooks/useTrialStatus
 *
 * React hook that wraps `GET /api/trial/me` (ROADS-445 FE-01).
 * Centralised so the route gates and the trial-countdown banner share
 * one fetch + one source of truth for the server-derived clock.
 *
 * Behaviour:
 *   - state === "loading" → first paint while the probe is in flight.
 *   - state === "ready"   → carries the {@link TrialStatus} snapshot.
 *   - state === "error"   → 5xx / network. Treated as "trial inactive"
 *                          by the gate so we fail closed (paywall) rather
 *                          than open (free access). 401 bubbles up via
 *                          the route's outer ProtectedRoute.
 */
import { useEffect, useRef, useState } from "react";

import { ApiError } from "@/lib/api";
import { getTrialStatus, type TrialStatus } from "@/lib/trial-api";

export type UseTrialStatusState =
  | { kind: "loading" }
  | { kind: "ready"; status: TrialStatus }
  | { kind: "error"; status: number | null; message: string };

export function useTrialStatus(): UseTrialStatusState {
  const [state, setState] = useState<UseTrialStatusState>({ kind: "loading" });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const status = await getTrialStatus();
        if (cancelled || !mountedRef.current) return;
        setState({ kind: "ready", status });
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        if (err instanceof ApiError) {
          setState({ kind: "error", status: err.status, message: err.message });
          return;
        }
        setState({
          kind: "error",
          status: null,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
